import type { LocalDate } from "@js-joda/core";
import { useEffect, useState } from "react";
import type { ScheduleApiData } from "web-apps/company/components/schedule/types/schedule.types";
import { useScheduleStore } from "web-apps/company/stores/schedule/scheduleStore";
import { isOk } from "utils/result";

type ScheduleDayShiftsState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly data: ScheduleApiData };

interface UseScheduleDayShiftsParams {
  readonly companyId: number | undefined;
  readonly date: LocalDate | null;
}

export const useScheduleDayShifts = ({
  companyId,
  date,
}: UseScheduleDayShiftsParams): ScheduleDayShiftsState => {
  const [, scheduleActions] = useScheduleStore();
  const [state, setState] = useState<ScheduleDayShiftsState>({
    status: "idle",
  });

  useEffect(() => {
    if (!date || !companyId) {
      setState({ status: "idle" });
      return;
    }
    let isActive = true;
    setState({ status: "loading" });
    const load = async (): Promise<void> => {
      const result = await scheduleActions.getScheduleDay({ companyId, date });
      if (!isActive) return;
      setState(
        isOk(result)
          ? { status: "ready", data: result.value }
          : { status: "error" },
      );
    };
    void load();
    return () => {
      isActive = false;
    };
  }, [companyId, date, scheduleActions]);

  return state;
};
